<template>
  <b-modal
    id="login-modal"
    modal-class="ts-auth-modal ts-login-modal"
    dialog-class="modal-dialog-centered modal-dialog-scrollable"
    header-class="border-0 p-4 pb-0 mb-3"
    body-class="px-4 pb-4 pt-0"
    hide-footer
    aria-labelledby="loginModalTitle"
    @show="onShow"
    @hidden="onHidden"
  >
    <template #modal-header="{ close }">
      <h2 id="loginModalTitle" class="modal-title ts-auth-title fw-bold">{{ translations[14408] }}</h2>
      <button class="btn-close" type="button" :aria-label="closeLabel" @click="close()"></button>
    </template>

    <form novalidate @submit.prevent="submit">
      <div class="ts-role-switch ts-login-role-switch d-flex gap-1 rounded-pill p-1 w-100" :class="{ 'is-support': role === roles.support }">
        <template v-for="item in roleOptions">
          <input
            :id="`loginRole-${item.value}`"
            :key="`input-${item.value}`"
            v-model="role"
            class="btn-check"
            type="radio"
            name="loginRole"
            :value="item.value"
          />
          <label
            :key="`label-${item.value}`"
            class="ts-role-option d-flex align-items-center justify-content-center text-center"
            :for="`loginRole-${item.value}`"
          >
            <i class="bi" :class="item.icon" aria-hidden="true"></i>
            {{ item.title }}
          </label>
        </template>
      </div>

      <div class="ts-login-body d-flex flex-column gap-2 mt-3">
        <div v-if="hasError" class="alert alert-danger mb-0" role="alert">{{ translations[2016] }}</div>

        <label class="visually-hidden" for="loginUsername">{{ translations[227] }}</label>
        <input
          id="loginUsername"
          v-model.trim="username"
          class="form-control"
          type="text"
          autocomplete="username"
          :placeholder="translations[227]"
          required
        />

        <label class="visually-hidden" for="loginPassword">{{ translations[228] }}</label>
        <div class="position-relative">
          <input
            id="loginPassword"
            v-model="password"
            class="form-control pe-5"
            :type="showPassword ? 'text' : 'password'"
            autocomplete="current-password"
            :placeholder="translations[228]"
            required
          />
          <button
            class="btn btn-link position-absolute top-50 end-0 translate-middle-y text-secondary"
            type="button"
            :aria-pressed="String(showPassword)"
            :aria-label="translations[228]"
            @click="showPassword = !showPassword"
          >
            <i class="bi" :class="showPassword ? 'bi-eye-slash' : 'bi-eye'" aria-hidden="true"></i>
          </button>
        </div>

        <!-- Support accounts have no self-service password reset (hidden by .is-support) -->
        <div class="ts-forgot text-end">
          <a class="ts-login-forgot-link small" href="#" @click.prevent="openResetPassword">{{ translations[10945] }}</a>
        </div>
      </div>

      <button class="btn btn-primary w-100 fw-bold mt-3" type="submit" :disabled="loading">
        <span v-if="loading" class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
        {{ translations[13726] }}
      </button>

      <div class="ts-login-footer text-center small mt-3">
        <span class="d-block">{{ translations[13730] }}</span>
        <a class="d-block" href="#" @click.prevent="openRegister" v-html="translations[13731]"></a>
        <div v-if="isKle" class="mt-2">{{ translations[16011] }}</div>
      </div>
    </form>
  </b-modal>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { AppModule, AuthTab } from '@/store/modules/app';
import { UserModule } from '@/store/modules/user';
import { groupNewCabinet } from '@/config/configuration';

type LoginRole = AuthTab.applicant | AuthTab.firm | AuthTab.support;

// One form for all three roles: a role only changes which account is signed in,
// which other roles are signed out first and where "forgot password" leads.
@Component
export default class LoginModal extends Vue {
  roles = { applicant: AuthTab.applicant, firm: AuthTab.firm, support: AuthTab.support } as const;

  role: LoginRole = AuthTab.applicant;

  username = '';

  password = '';

  showPassword = false;

  loading = false;

  hasError = false;

  get translations() {
    return AppModule.translation;
  }

  get sid() {
    return AppModule.sid;
  }

  get closeLabel() {
    return AppModule.translation[2511];
  }

  get isKle() {
    return ['kle', 'bbg', 'en', 'bbw', 'bfz'].includes(this.sid);
  }

  get roleOptions() {
    return [
      { value: AuthTab.applicant, icon: 'bi-person', title: this.translations[11115] },
      { value: AuthTab.firm, icon: 'bi-building', title: this.translations[13937] },
      { value: AuthTab.support, icon: 'bi-headset', title: this.translations[13727] },
    ];
  }

  // Opened for a specific role (e.g. ?auth=f-login or a "register as company" flow) → preselect it
  onShow() {
    // selectedAuthTab is '' when nothing was chosen
    this.role = (AppModule.selectedAuthTab as LoginRole) || AuthTab.applicant;
    this.hasError = false;
  }

  onHidden() {
    AppModule.SET_SELECTED_AUTH_TAB(AuthTab.default);
    UserModule.SET_OPEN_AGENT_PAGE(false);
    this.password = '';
    this.showPassword = false;
  }

  // Only one role can be signed in at a time
  signOutOtherRoles() {
    if (this.role !== AuthTab.applicant && UserModule.accessTokenApplicant) UserModule.LogOutApplicant(this.sid);
    if (this.role !== AuthTab.firm && UserModule.accessTokenCompany) UserModule.LogOutCompany(this.sid);
    if (this.role !== AuthTab.support && UserModule.accessTokenConsultant) UserModule.LogOutConsultant(this.sid);
  }

  signIn(credentials: { username: string, password: string, sid: string }) {
    if (this.role === AuthTab.firm) return UserModule.LoginCompany(credentials);
    if (this.role === AuthTab.support) return UserModule.LoginConsultant(credentials);
    return UserModule.LoginApplicant(credentials);
  }

  async submit() {
    if (!this.username || !this.password || this.loading) return;
    this.loading = true;
    this.hasError = false;
    try {
      this.signOutOtherRoles();
      await this.signIn({ username: this.username, password: this.password, sid: this.sid });
    } catch {
      this.hasError = true;
      return;
    } finally {
      this.loading = false;
    }

    // Company accounts of the new-cabinet sids log into the alpha cabinet
    if (this.role === AuthTab.firm && groupNewCabinet(this.sid)) {
      UserModule.SetAlphaLogin('login-alpha');
    } else if (this.role === AuthTab.firm) {
      UserModule.RemoveAlphaLogin();
    }

    this.username = '';
    if (UserModule.isRedirectToCabinet) {
      // the applicant can land directly on the job agent page (set by the filters auth alert)
      const agent = this.role === AuthTab.applicant && UserModule.openAgentPageOnCabinet;
      await this.$router.push({ name: 'Cabinet', query: { lang: AppModule.language, ...(agent && { agent: '1' }) } });
    }
    this.$bvModal.hide('login-modal');
  }

  openResetPassword() {
    const modalId = this.role === AuthTab.firm ? 'company-reset-password' : 'applicant-reset-password';
    this.$bvModal.hide('login-modal');
    this.$bvModal.show(modalId);
  }

  openRegister() {
    this.$bvModal.hide('login-modal');
    this.$bvModal.show('register-modal');
  }
}
</script>
